/**
 * HOME — one composer, and the work as a board (owner-accepted design,
 * 2026-10-09: "an operating system for professional work, not a
 * reconciliation tool", calm in the way Ramp and Stripe are calm).
 *
 * NOT A DASHBOARD. No chart, no analytics, no activity feed, no counters of
 * things nobody asked about. One place to say what the work is, and the work
 * already in flight laid out by the state it is actually in.
 *
 * THE COLUMNS ARE THE SERVER'S. Their order, their labels, their hints and
 * which work falls in which one are all decided in `@leveri/work-os` and sent
 * already grouped — an empty group never arrives, because a board of empty
 * rails is noise. This screen renders `groups[]` in the order it was given and
 * does not know how many of them exist or what they are called.
 *
 * WHAT THE COMPOSER ASKS, AND WHY IT IS THE LEAST IT COULD ASK. Creating work
 * takes an instruction and the engagement it belongs to, and nothing else:
 * `POST /api/work` accepts exactly those two. The client and the engagement
 * sit INSIDE the box, as chips in its bottom bar, because they are part of the
 * same act as the sentence — set apart under the box they read as a second
 * form (the owner's own objection to the screen this replaces). The period,
 * the currency and the control account are not asked here: the engagement
 * answers the first two, and the third is a choice from the records
 * themselves, made inside the file once they are in it.
 *
 * Whether the sentence SAYS enough is the server's rule (`CreateWorkRequest`),
 * and its refusal is shown as written: a second copy of that rule here would be
 * a second answer to the same question.
 */
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mic, Plus } from "lucide-react";
import {
  ClientListResponse, EncloseRecordResponse, EngagementListResponse, WorkBoardResponse, WorkResponse,
  type Client, type Engagement, type RecordKind, type RecordRole, type WorkBoardResponse as Board, type WorkSummary,
} from "@leveri/contracts";
import {
  BOARD_CARD, BoardColumn, BoardGrid, Button, CHIP_ACTION, ChipSelect, ComposerBar, ComposerFrame, FileButton, IconButton,
  KindSelect, Marker, Notice, Reading, RecordChip, SERIF_OPSZ, StateText, markerTone,
} from "../file/index.js";
import { call, enclose, failureMessage } from "../api.js";
import { Desk } from "./shell.js";
import { useVocabularies } from "./vocabularies.js";
import { DICTATION_PRIVACY, useDictation } from "./dictation.js";
import { ACCEPT_RECORDS, withChosen, type Chosen } from "./work/enclose.js";

/**
 * DICTATION IS BUILT AND NOT SHOWN. The browser's speech service sends the
 * audio to the browser vendor's servers, and whether a person's voice may
 * leave the browser at all is the OWNER'S privacy decision, not this
 * screen's. Until that ruling is made, the microphone is not drawn. Turning
 * this on is the whole change, and it is a ruling, not a refactor.
 */
const DICTATION_ENABLED = false;

export function BoardScreen() {
  const navigate = useNavigate();
  const [board, setBoard] = useState<Board | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      setBoard(await call("GET", "/api/work", WorkBoardResponse));
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const open = useCallback((work: WorkSummary) => navigate(`/work/${work.id}`), [navigate]);

  return (
    <Desk current="work" measure="wide">
      {/* THE ASK. Centred, because it is the one thing this page is for. */}
      <section aria-labelledby="ask" className="mx-auto w-full max-w-[820px] lg:pt-4">
        <h1
          id="ask"
          className="text-center font-serif text-[30px] font-normal leading-[1.15] tracking-[-0.015em] text-ink [text-wrap:balance] sm:text-[34px]"
          style={SERIF_OPSZ}
        >
          What should Leveri work on?
        </h1>
        <Composer onOpened={open} />
      </section>

      {/* THE WORK, AS A BOARD. */}
      <section aria-labelledby="your-work" className="mt-14">
        <div className="flex items-baseline gap-3">
          <h2 id="your-work" className="text-[15px] font-semibold text-ink">Your work</h2>
          {board && board.total > 0 && <span className="font-mono text-[13px] text-stone">{board.total}</span>}
        </div>

        {failure && (
          <div className="mt-4">
            <Notice>{failure}</Notice>
            <button type="button" onClick={() => void load()} className="mt-2 text-[13px] font-medium text-ink underline-offset-4 hover:underline">
              Try again
            </button>
          </div>
        )}

        {!board && !failure && <div className="mt-4"><Reading>Loading the work…</Reading></div>}

        {board && board.groups.length === 0 && (
          <div className="mt-4 border-t border-rule pt-8 text-center">
            <p className="text-[15px] font-medium text-ink">Nothing in flight yet</p>
            <p className="mx-auto mt-1.5 max-w-[460px] text-sm text-stone">
              Say what the work is above and Leveri will open a file for it. It will appear here, grouped by where it stands.
            </p>
          </div>
        )}

        {board && board.groups.length > 0 && (
          <BoardGrid className="mt-5">
            {board.groups.map((group) => (
              <BoardColumn key={group.key} id={`group-${group.key}`} label={group.label} count={group.work.length} hint={group.hint}>
                {group.work.map((work) => <WorkCard key={work.id} work={work} />)}
              </BoardColumn>
            ))}
          </BoardGrid>
        )}
      </section>
    </Desk>
  );
}

/**
 * One piece of work on the board.
 *
 * Every word and every number here is the projection's: the title the
 * accountant wrote, the client and engagement the work is booked to, the
 * period as the engagement states it, and the status word with the tone the
 * server decided. Nothing is re-phrased and nothing is counted again. A long
 * title or name is clipped on the card and whole in the file it opens.
 */
function WorkCard({ work }: { work: WorkSummary }) {
  return (
    <li>
      <Link to={`/work/${work.id}`} className={BOARD_CARD}>
        <span className="line-clamp-3 block text-sm font-medium leading-snug text-ink [overflow-wrap:anywhere]" title={work.title}>
          {work.title}
        </span>
        <span className="mt-1 block truncate text-[12px] text-stone" title={`${work.clientName} · ${work.engagementTitle}`}>
          {work.clientName} · {work.engagementTitle}
        </span>
        {/* The period as one phrase, read for a person on the server. The
            machine values stay on the wire and sit in the title. */}
        <span className="block text-[12px] text-stone" title={`${work.periodStart} – ${work.cutOff}`}>
          {work.periodText} · {work.currency}
        </span>
        <span className="mt-2.5 flex">
          <StateText tone={markerTone(work.tone)} className="text-[13px]">{work.statusLabel}</StateText>
        </span>
        {/* Counts as labelled figures rather than sentences: the server sent
            numbers, and a sentence built around one would be this screen
            stating something the projection did not. */}
        <span className="mt-1 block text-[12px] text-stone">
          <Count label="Records">{work.recordCount}</Count> · <Count label="Open requests">{work.openRequestCount}</Count>
          {work.workpaperVersion !== null && <> · <Count label="Workpaper">v{work.workpaperVersion}</Count></>}
        </span>
      </Link>
    </li>
  );
}

/** A labelled number, kept on one line, the number in the figure face. */
function Count({ label, children }: { label: string; children: ReactNode }) {
  return <span className="whitespace-nowrap">{label} <span className="font-mono text-ink-soft">{children}</span></span>;
}

// ─── the composer ───────────────────────────────────────────────────────────

/**
 * WHAT THE SERVER'S READER CAN READ TODAY, as the file picker already offers
 * it. A file of any other kind is SHOWN, with the plain reason it cannot be
 * used, and is never sent: the reader would refuse it, and a refused file is
 * recorded against the work as permanent history — a screenshot pasted by
 * habit must not become a refusal on a client's file. When the reader learns
 * a new format, this list widens with the picker's.
 */
const READABLE = ACCEPT_RECORDS.split(",").map((extension) => extension.trim().toLowerCase());
const isReadable = (file: File) => READABLE.some((extension) => file.name.toLowerCase().endsWith(extension));
const sameFile = (a: File, b: File) => a.name === b.name && a.size === b.size;

const NO_KINDS: readonly RecordKind[] = [];
const NO_ROLES: readonly RecordRole[] = [];

/**
 * THE CONTEXT THE LAST PIECE OF WORK WAS OPENED UNDER, remembered for this tab
 * only and never written to browser storage. Coming back from a file to open
 * the next piece of work for the same client is the ordinary case; the chips
 * propose it, and it is used only if it is still in the lists the server sends.
 */
let lastContext: { clientId: string; engagementId: string } | null = null;

function Composer({ onOpened }: { onOpened: (work: WorkSummary) => void }) {
  const [instruction, setInstruction] = useState("");
  /**
   * RECORDS ALONGSIDE THE INSTRUCTION (R5.1). They cannot be attached BEFORE
   * the work — a record is enclosed INTO a work, and that anchor is the whole
   * point of ADR-0009 — so the work is opened first and the files go into it
   * in the same movement. Each kind is still DECLARED here, from the
   * vocabulary the server serves; nothing is guessed from a filename.
   */
  const [chosen, setChosen] = useState<Chosen[]>([]);
  /** Files the reader cannot read yet: shown with the reason, never sent. */
  const [unreadable, setUnreadable] = useState<File[]>([]);
  const { terms, failure: termsFailure } = useVocabularies();
  const kinds = terms?.recordKinds ?? NO_KINDS;
  const [clients, setClients] = useState<Client[] | null>(null);
  const [clientsFailed, setClientsFailed] = useState(false);
  const [clientId, setClientId] = useState("");
  const [engagements, setEngagements] = useState<Engagement[] | null>(null);
  const [engagementId, setEngagementId] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * THE WORK, ONCE IT EXISTS. If the work opens but its files do not arrive,
   * the work is still real — so trying again adds the files to THIS work
   * rather than opening a second copy of it.
   */
  const [opened, setOpened] = useState<WorkSummary | null>(null);
  const [dragging, setDragging] = useState(false);

  const speak = useCallback((words: string) => {
    setInstruction((written) => (written.trim() ? `${written.replace(/\s+$/, "")} ${words}` : words));
  }, []);
  const dictation = useDictation(speak);
  const offerDictation = DICTATION_ENABLED && dictation.available;

  useEffect(() => {
    let abandoned = false;
    call("GET", "/api/clients", ClientListResponse)
      .then((r) => {
        if (abandoned) return;
        setClients(r.clients);
        const memory = lastContext;
        if (memory && r.clients.some((client) => client.id === memory.clientId)) {
          setClientId((current) => current || memory.clientId);
        }
      })
      .catch((error: unknown) => {
        if (abandoned) return;
        setClientsFailed(true);
        setFailure(failureMessage(error));
      });
    return () => { abandoned = true; };
  }, []);

  // Engagements are this client's own, and are not asked for before there is a
  // client to ask about. A new client clears the engagement: an engagement
  // belongs to the client it was chosen under, and to nothing else.
  useEffect(() => {
    let abandoned = false;
    setEngagements(null);
    setEngagementId("");
    if (!clientId) return;
    call("GET", `/api/clients/${clientId}/engagements`, EngagementListResponse)
      .then((r) => {
        if (abandoned) return;
        setEngagements(r.engagements);
        const memory = lastContext;
        if (memory && memory.clientId === clientId && r.engagements.some((engagement) => engagement.id === memory.engagementId)) {
          setEngagementId(memory.engagementId);
        }
      })
      .catch((error: unknown) => { if (!abandoned) setFailure(failureMessage(error)); });
    return () => { abandoned = true; };
  }, [clientId]);

  const addFiles = useCallback((files: readonly File[]) => {
    const readable = files.filter(isReadable);
    const other = files.filter((file, index) => !isReadable(file) && files.findIndex((f) => sameFile(f, file)) === index);
    if (readable.length > 0) setChosen((held) => withChosen(held, readable, NO_ROLES));
    if (other.length > 0) setUnreadable((held) => [...held, ...other.filter((file) => !held.some((h) => sameFile(h, file)))]);
  }, []);

  /** Files can arrive only once the kinds have — each one must be said for what it is. */
  const canAdd = !busy && kinds.length > 0;

  /**
   * DROP ANYWHERE, PASTE ANYWHERE. A file dropped on the page lands in the
   * composer — and a file dropped anywhere is never allowed to make the
   * browser navigate away to it, which would throw away what was written.
   */
  useEffect(() => {
    let depth = 0;
    const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes("Files");
    const onEnter = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth += 1;
      setDragging(true);
    };
    const onOver = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = canAdd ? "copy" : "none";
    };
    const onLeave = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onDrop = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      depth = 0;
      setDragging(false);
      if (canAdd) addFiles(Array.from(event.dataTransfer?.files ?? []));
    };
    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length === 0 || !canAdd) return;
      // Cells copied from a spreadsheet arrive as text AND as a picture of
      // the cells. Pasted into the writing area, the text is what was meant.
      const intoText = event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement;
      if (intoText && Array.from(event.clipboardData?.types ?? []).includes("text/html")) return;
      event.preventDefault();
      addFiles(files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    document.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
      document.removeEventListener("paste", onPaste);
      setDragging(false);
    };
  }, [addFiles, canAdd]);

  const ready = Boolean(instruction.trim()) && Boolean(engagementId) && chosen.every((c) => c.role !== "");
  /** Once the work exists, what it is and whose it is are settled. */
  const locked = busy || opened !== null;

  async function send() {
    if (busy || !ready) return;
    dictation.stop();
    setBusy(true);
    setFailure(null);
    let work = opened;
    try {
      if (!work) {
        work = (await call("POST", "/api/work", WorkResponse, { engagementId, instruction })).work;
        lastContext = { clientId, engagementId };
      }
      if (chosen.length > 0) {
        await enclose(
          `/api/work/${work.id}/records`,
          EncloseRecordResponse,
          chosen.map((c) => c.file),
          chosen[0]!.role as string,
          chosen.map((c) => c.role as string)
        );
      }
      onOpened(work);
    } catch (error) {
      if (work) setOpened(work);
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  const client = clients?.find((c) => c.id === clientId) ?? null;
  const engagement = engagements?.find((e) => e.id === engagementId) ?? null;
  const noClients = clients !== null && clients.length === 0;
  const noEngagements = Boolean(clientId) && engagements !== null && engagements.length === 0;
  const unsaid = !chosen.every((c) => c.role !== "");

  // ONE quiet line beside the chips: the next thing to do, and only that.
  const hint = noClients
    ? "Work is always opened for a client."
    : noEngagements
      ? "This client has no engagements yet."
      : !engagementId && !clientsFailed
        ? "Choose who this work is for."
        : unsaid
          ? "Say what each file is before opening the work."
          : null;

  return (
    <div className="mt-6">
      <ComposerFrame dragging={dragging}>
        <label htmlFor="instruction" className="sr-only">What the work is</label>
        <textarea
          id="instruction"
          rows={2}
          value={instruction}
          readOnly={locked}
          onChange={(event) => setInstruction(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              void send();
            }
          }}
          placeholder="Say what the work is, in your own words…"
          className="block max-h-[40vh] min-h-[52px] w-full resize-none border-0 bg-transparent p-0 text-[17px] leading-[1.5] text-ink placeholder:text-stone focus-visible:outline-none [field-sizing:content]"
        />

        {offerDictation && dictation.listening && (
          <div role="status" className="flex items-start gap-2 text-[12px] text-ink-soft">
            <Marker tone="progress" className="mt-[5px]" />
            <div>
              <span className="font-medium text-ink">Listening.</span> {DICTATION_PRIVACY}
              {dictation.heard && <div className="mt-0.5 italic text-stone">{dictation.heard}</div>}
            </div>
          </div>
        )}

        {/* THE FILES IN HAND, each saying what it is. */}
        {(chosen.length > 0 || unreadable.length > 0) && (
          <ul aria-label="Files for this work" className="flex flex-wrap gap-2">
            {chosen.map((held) => {
              const kind = kinds.find((k) => k.role === held.role);
              return (
                <RecordChip
                  key={`${held.file.name}-${held.file.size}`}
                  name={held.file.name}
                  disabled={busy}
                  onRemove={() => setChosen((list) => list.filter((c) => !sameFile(c.file, held.file)))}
                >
                  <KindSelect
                    label={`What ${held.file.name} is`}
                    display={kind?.label ?? "Say what this is"}
                    empty={held.role === ""}
                    value={held.role}
                    disabled={busy}
                    onChange={(event) => {
                      const role = event.target.value as RecordRole | "";
                      setChosen((list) => list.map((c) => (sameFile(c.file, held.file) ? { ...c, role } : c)));
                    }}
                  >
                    <option value="">Say what this is</option>
                    {kinds.map((k) => <option key={k.role} value={k.role}>{k.label}</option>)}
                  </KindSelect>
                </RecordChip>
              );
            })}
            {unreadable.map((file) => (
              <RecordChip
                key={`unread-${file.name}-${file.size}`}
                name={file.name}
                disabled={busy}
                onRemove={() => setUnreadable((list) => list.filter((f) => !sameFile(f, file)))}
              >
                <span className="text-[12px] text-att-text">Leveri reads spreadsheets today — this file can&rsquo;t be added yet</span>
              </RecordChip>
            ))}
          </ul>
        )}

        <ComposerBar
          context={
            <>
              {noClients ? (
                <Link to="/clients" className={CHIP_ACTION}>
                  <span>Client:</span>
                  <span className="font-semibold text-ink">Add your first client</span>
                  <ArrowRight aria-hidden="true" size={14} className="text-stone" />
                </Link>
              ) : (
                <ChipSelect
                  label="Client"
                  className="sm:max-w-[16rem]"
                  display={client?.name ?? (clients === null ? (clientsFailed ? "Unavailable" : "Loading…") : "Choose")}
                  empty={!client}
                  value={clientId}
                  disabled={clients === null || locked}
                  onChange={(event) => setClientId(event.target.value)}
                >
                  <option value="">{clients === null ? "Loading…" : "Choose a client"}</option>
                  {(clients ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </ChipSelect>
              )}

              {!noClients && (noEngagements ? (
                <Link to={`/clients/${clientId}`} className={CHIP_ACTION}>
                  <span>Engagement:</span>
                  <span className="font-semibold text-ink">Add one for this client</span>
                  <ArrowRight aria-hidden="true" size={14} className="text-stone" />
                </Link>
              ) : (
                <ChipSelect
                  label="Engagement"
                  className="sm:max-w-[24rem]"
                  display={
                    engagement
                      ? <>{engagement.title} <span className="font-normal text-stone">· {engagement.periodText}</span></>
                      : !clientId ? "Choose a client first" : engagements === null ? "Loading…" : "Choose"
                  }
                  empty={!engagement}
                  value={engagementId}
                  disabled={!clientId || engagements === null || locked}
                  onChange={(event) => setEngagementId(event.target.value)}
                >
                  <option value="">{!clientId ? "Choose a client first" : engagements === null ? "Loading…" : "Choose an engagement"}</option>
                  {(engagements ?? []).map((e) => (
                    <option key={e.id} value={e.id}>{e.title} · {e.periodText}</option>
                  ))}
                </ChipSelect>
              ))}

            </>
          }
          note={hint}
          actions={
            <>
              <FileButton
                accept={ACCEPT_RECORDS}
                multiple
                size="icon"
                label="Add spreadsheets"
                title="Add spreadsheets"
                disabled={!canAdd}
                onFiles={addFiles}
              >
                <Plus aria-hidden="true" size={18} />
              </FileButton>
              {offerDictation && (
                <>
                  <IconButton
                    label={dictation.listening ? "Stop dictating" : "Dictate the task"}
                    title={DICTATION_PRIVACY}
                    aria-pressed={dictation.listening}
                    aria-describedby="dictation-privacy"
                    disabled={locked}
                    onClick={dictation.toggle}
                  >
                    <Mic size={18} />
                  </IconButton>
                  <span id="dictation-privacy" className="sr-only">{DICTATION_PRIVACY}</span>
                </>
              )}
              <Button size="lg" disabled={busy || !ready} onClick={() => void send()} className="max-sm:flex-1">
                Open the work
              </Button>
            </>
          }
        />
      </ComposerFrame>

      <div className="mt-3 flex flex-col gap-2">
        {failure && <Notice>{failure}</Notice>}
        {opened && !busy && (
          <p className="text-[13px] text-ink-soft">
            The work is open, but the files weren&rsquo;t added. Try again, or{" "}
            <Link to={`/work/${opened.id}`} className="font-medium text-ink underline underline-offset-4">go to the work</Link>{" "}
            and add them there.
          </p>
        )}
        {termsFailure && <Notice tone="attention">{termsFailure}</Notice>}
        {offerDictation && dictation.problem && <Notice tone="attention">{dictation.problem}</Notice>}
        {busy && <Reading>{chosen.length > 0 ? "Opening the work and adding the files…" : "Opening the work…"}</Reading>}
        <p className="text-center text-[12px] text-stone" aria-live="polite">
          {dragging ? (
            "Let go to add them to the work"
          ) : (
            <>
              <span className="sm:hidden">Add spreadsheets with + · nothing starts until you say so</span>
              <span className="max-sm:hidden">Drop or paste spreadsheets anywhere · nothing starts until you say so</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
